// COPIED from Remote-Leverage/hriq@3b50a234afdd packages/design-system/components/ui/v2/table-panel.tsx by apps/web/scripts/sync-design-system.mjs. Do not edit: change it in Hriq and re-sync.
// @ts-nocheck -- type-checked in Hriq under its own compiler options (the CRM's are stricter).
import { Table } from "@repo/design-system/components/ui/table";
import { cn } from "@repo/design-system/lib/utils";
import type { ComponentProps } from "react";

/**
 * v2 Table Panel — the rounded, softly graded panel that holds a v2
 * `Cell` + `Row` table, from the Figma "Documents | RL Manager" file,
 * "2.0.0 Documents List" frame, "Checklist" container `2023:3656`: 16px
 * radius, 12px padding, a top-to-bottom gray gradient, and white 60px rows
 * spaced 8px apart sitting on it.
 *
 * Renders v1 `Table` inside the panel with the `border-separate` +
 * `border-spacing-y-2` setup `v2/cell-row.tsx` needs for per-row rounded
 * corners, so a screen only composes rows:
 *
 *   <TablePanel>
 *     <TableHeader><HeaderRow>...</HeaderRow></TableHeader>
 *     <TableBody><Row><Cell>...</Cell></Row></TableBody>
 *   </TablePanel>
 *
 * Vertical padding is 4px, not Figma's 12px: the table's 8px
 * border-spacing already sits above the header and below the last row, so
 * 4 + 8 lands on Figma's 12px edge. (Not a negative margin on the table:
 * v1 wraps it in an `overflow-x-auto` div, which also clips/scrolls on y,
 * so a table that overhangs its wrapper shows a vertical scrollbar.)
 *
 * `className` reaches the panel; `tableClassName` reaches the `<table>`.
 *
 * `variant="flush"` is for a table that already sits inside a card: no grey
 * tray (grey inside a white card reads muddy), no inset padding, rows edge to
 * edge with hairline dividers and square corners, and a hairline under the
 * header. Rows keep the Cell + Row hover/selected tints.
 */

type TablePanelProps = ComponentProps<"div"> & {
  tableClassName?: string;
  variant?: "panel" | "flush";
};

const FLUSH_PANEL_CLASS = "rounded-none bg-none p-0";
const FLUSH_TABLE_CLASS = [
  "border-spacing-y-0",
  "[&_[data-slot=cell]]:rounded-none!",
  "[&_[data-slot=cell]]:border-border [&_tbody>tr:not(:last-child)>[data-slot=cell]]:border-b",
  "[&_[data-slot=cell-header]]:border-border [&_[data-slot=cell-header]]:border-b",
].join(" ");

function TablePanel({
  className,
  tableClassName,
  variant = "panel",
  children,
  ...props
}: TablePanelProps) {
  const flush = variant === "flush";
  return (
    <div
      className={cn(
        "ds-v2 w-full overflow-hidden rounded-[16px] px-3 py-1",
        "bg-linear-to-b from-(--v2-table-panel-bg-from) from-[8.936%] to-(--v2-table-panel-bg-to)",
        flush && FLUSH_PANEL_CLASS,
        className
      )}
      data-slot="table-panel"
      data-variant={variant}
      {...props}
    >
      <Table
        className={cn(
          "border-separate border-spacing-y-2",
          flush && FLUSH_TABLE_CLASS,
          tableClassName
        )}
      >
        {children}
      </Table>
    </div>
  );
}

export { TablePanel };
export type { TablePanelProps };
